<template>
    <nav :aria-label="label">
        <BaseCard padding="none" radius="md">
            <div ref="list" class="items">
                <!-- The note is a tooltip rather than a second line: a rail of permanent explanations is
                     busier than the items it is labelling. A disabled button swallows its own pointer
                     events, so the span outside it is what the pointer lands on.
                     -->
                <BaseTooltip v-for="item in items" :key="item.id" :disabled="!item.note" side="right" trigger-aria-label="">
                    <template #trigger>
                        <span class="item-slot">
                            <BaseButton
                                theme="tertiary"
                                class="item"
                                :aria-current="modelValue === item.id ? 'step' : undefined"
                                :aria-label="item.accessibleLabel ?? item.label"
                                :disabled="item.disabled"
                                @click="modelValue = item.id">
                                <component :is="item.icon" v-if="typeof item.icon === 'object'" class="item-icon" />
                                <i v-else-if="item.icon" class="bi item-icon" :class="item.icon" aria-hidden="true"></i>
                                <span class="item-label">{{ item.label }}</span>
                                <i v-if="item.completed" class="bi bi-check-lg item-tick" aria-hidden="true"></i>
                            </BaseButton>
                        </span>
                    </template>
                    <template #content>{{ item.note }}</template>
                </BaseTooltip>
            </div>
        </BaseCard>
    </nav>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { type Component, nextTick, useTemplateRef, watch } from "vue";

export interface SectionNavItem {
    id: string;
    label: string;
    accessibleLabel?: string;
    /** String icons must be Bootstrap icon classes. */
    icon?: string | Component;
    completed?: boolean;
    /** Renders the item greyed and unclickable. Pair it with `note`, which says why. */
    disabled?: boolean;
    /** Shown on hover, for the condition that unlocks a disabled item. */
    note?: string;
}

defineProps<{ label: string; items: SectionNavItem[] }>();
const modelValue = defineModel<string>();
const list = useTemplateRef<HTMLElement>("list");
watch(
    modelValue,
    async () => {
        await nextTick();
        list.value?.querySelector("[aria-current]")?.scrollIntoView({ block: "nearest", inline: "center" });
    },
    { immediate: true },
);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .items {
        @apply flex gap-2 overflow-x-auto p-2.5 lg:flex-col lg:gap-2.5;
    }

    /* The pointer target for a disabled item's tooltip, and the flex child the items row lays out. */
    .item-slot {
        @apply flex shrink-0 lg:w-full;
    }

    .item {
        @apply scaffold-body-small flex items-center gap-2.5 rounded-md px-2.5 py-2.5 text-left whitespace-nowrap text-nav-item-text hover:bg-nav-item-hover-bg hover:text-nav-item-text-hover lg:w-full lg:whitespace-normal;
    }

    .item[aria-current] {
        @apply bg-nav-item-active-bg text-nav-item-active-text;
    }

    /* Passing pointer events through is what lets the wrapping span open the tooltip. */
    .item:disabled {
        @apply pointer-events-none bg-transparent text-ink-muted;
    }

    .item-icon {
        @apply shrink-0;
    }

    .item-label {
        @apply min-w-0 flex-1;
    }

    .item-tick {
        @apply shrink-0 text-status-success;
    }
}
</style>
